/** Client-side PDF/DOCX text extraction for resume + job-document autofill. */

const DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;
const TEXT_MAX_CHARS = 12000;

const PDF_MIME = new Set(["application/pdf", "application/x-pdf"]);
const DOCX_MIME = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
]);

export type DocumentKind = "pdf" | "docx";
/** @deprecated Use DocumentKind */
export type ResumeKind = DocumentKind;

export function getDocumentKind(file: File): DocumentKind | null {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf") || PDF_MIME.has(file.type)) return "pdf";
  if (name.endsWith(".docx") || DOCX_MIME.has(file.type)) return "docx";
  return null;
}

/** @deprecated Use getDocumentKind */
export function getResumeKind(file: File): DocumentKind | null {
  return getDocumentKind(file);
}

export function validateDocumentFile(
  file: File,
  options: { label?: string } = {},
): string | null {
  const label = options.label ?? "Document";
  if (!getDocumentKind(file)) {
    return `${label} must be a PDF or DOCX file`;
  }
  if (file.size <= 0) {
    return `${label} file is empty`;
  }
  if (file.size > DOCUMENT_MAX_BYTES) {
    return `${label} must be 5 MB or less`;
  }
  return null;
}

export function validateResumeFile(file: File): string | null {
  return validateDocumentFile(file, { label: "Resume" });
}

function normalizeText(raw: string): string {
  return raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, TEXT_MAX_CHARS);
}

let pdfWorkerReady = false;

async function ensurePdfWorker(pdfjs: typeof import("pdfjs-dist")): Promise<void> {
  if (pdfWorkerReady) return;
  try {
    const { default: PdfWorker } = await import("./pdfWorker?worker");
    pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
  } catch {
    const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  }
  pdfWorkerReady = true;
}

async function extractPdfText(file: File): Promise<string> {
  await import("./readableStreamAsyncIterator");
  const pdfjs = await import("pdfjs-dist");
  await ensurePdfWorker(pdfjs);

  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const parts: string[] = [];
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    const items = Array.isArray(content?.items) ? content.items : [];
    const pageText = items
      .map((item) => (item && "str" in item ? item.str : ""))
      .filter(Boolean)
      .join(" ");
    if (pageText.trim()) parts.push(pageText);
  }
  return parts.join("\n");
}

async function extractDocxText(file: File): Promise<string> {
  const mammoth = await import("mammoth");
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || "";
}

export async function extractDocumentText(
  file: File,
  options: { label?: string } = {},
): Promise<string> {
  const label = options.label ?? "document";
  const kind = getDocumentKind(file);
  if (!kind) {
    throw new Error(`${label} must be a PDF or DOCX file`);
  }
  const raw = kind === "pdf" ? await extractPdfText(file) : await extractDocxText(file);
  const text = normalizeText(raw);
  if (text.length < 40) {
    throw new Error(
      `Could not read text from this ${label} (it may be a scanned image). Fill the form manually.`,
    );
  }
  return text;
}

export async function extractResumeText(file: File): Promise<string> {
  return extractDocumentText(file, { label: "resume" });
}

export { DOCUMENT_MAX_BYTES, DOCUMENT_MAX_BYTES as RESUME_MAX_BYTES };
